import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../client";
import { readApiErrorEnvelope } from "../apiErrorEnvelope";
import {
  PriceDivergenceRowDto,
  PriceDivergenceSummaryDto,
  PriceDivergenceKind,
  SetProductPriceRequest,
  SetProductPriceResponse,
  SyncProductPricesRequest,
} from "../generated/api-client";
import { PriceDivergenceReportData, mergeSyncedRows } from "./priceDivergenceMerge";

export { PriceDivergenceRowDto, PriceDivergenceSummaryDto, PriceDivergenceKind };

const QUERY_KEYS = {
  divergence: ["product-pricing", "divergence"] as const,
};

// Fetches the divergence report. `useSetProductPrice` below invalidates this query on a
// successful write, and also on a `ProductPriceFlexiWriteFailed` partial failure, so an
// edited row reloads from live Shoptet/Flexi data instead of trusting the value the
// operator just typed or silently showing a stale "in agreement" state.
export const usePriceDivergenceReport = () =>
  useQuery<PriceDivergenceReportData>({
    queryKey: QUERY_KEYS.divergence,
    queryFn: async () => {
      const response = await getAuthenticatedApiClient().productPricing_GetDivergenceReport();
      return {
        rows: response.rows ?? [],
        summary: response.summary,
      };
    },
  });

export interface SetProductPriceInput {
  productCode: string;
  priceWithVat: number;
}

const GENERIC_SET_PRICE_ERROR = "Cenu se nepodařilo uložit.";

/**
 * Writes straight through to the live Shoptet store and the live ABRA Flexi ERP — there is
 * no sandbox and no server-side price ceiling. The thrown `Error` always carries the
 * backend's `errorCode` (a string) as a property so the caller can translate it; a
 * `ProductPriceFlexiWriteFailed` means Shoptet WAS updated but Flexi was not, so the two
 * systems now hold different prices.
 */
const setProductPrice = async ({
  productCode,
  priceWithVat,
}: SetProductPriceInput): Promise<SetProductPriceResponse> => {
  try {
    const response = await getAuthenticatedApiClient().productPricing_SetPrice(
      productCode,
      new SetProductPriceRequest({ productCode, priceWithVat }),
    );
    if (response.success === false) {
      throw Object.assign(new Error(GENERIC_SET_PRICE_ERROR), { errorCode: response.errorCode });
    }
    return response;
  } catch (error) {
    if (error instanceof Error && (error as { errorCode?: string }).errorCode) {
      throw error;
    }
    const envelope = readApiErrorEnvelope(error);
    throw Object.assign(new Error(GENERIC_SET_PRICE_ERROR), { errorCode: envelope?.errorCode });
  }
};

// The one failure mode where Shoptet was already written even though the mutation as a
// whole failed. Without a refetch here, the row would keep rendering its pre-edit Shoptet
// price next to the pre-edit Flexi price — the two would still match, so the row would
// render InAgreement (green) even though the live systems have actually diverged. The
// persistent row-level alert would then be the only thing contradicting the row it sits in.
// The other four error codes all mean nothing was written anywhere, so invalidating on
// those would just be a pointless pair of live API calls against Shoptet and Flexi on every
// validation-style failure.
const FLEXI_WRITE_FAILED_ERROR_CODE = "ProductPriceFlexiWriteFailed";

export const useSetProductPrice = () => {
  const queryClient = useQueryClient();

  return useMutation<SetProductPriceResponse, Error, SetProductPriceInput>({
    mutationFn: setProductPrice,
    onSuccess: () => {
      // Reload from live Shoptet/Flexi data rather than trusting the local value.
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.divergence });
    },
    onError: (error) => {
      const errorCode = (error as { errorCode?: string })?.errorCode;
      if (errorCode === FLEXI_WRITE_FAILED_ERROR_CODE) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.divergence });
      }
    },
  });
};

export const GENERIC_SYNC_ERROR = "Ceny se nepodařilo synchronizovat.";

/** What one sync did, as the backend reports it. */
export interface SyncProductPricesOutcome {
  /** The selection's rows as they stand after the writes, to merge into the cached report. */
  rows: PriceDivergenceRowDto[];
  /** How many Flexi prices were overwritten with the Shoptet price. */
  writtenCount: number;
  /** How many rows were written to and did not take it; they stay divergent on screen. */
  failedCount: number;
  /** How many rows still need a write, because the run spent its write budget first. */
  remainingCount: number;
}

/**
 * Pushes Shoptet's prices into Flexi for one selection of products and folds the resulting
 * rows into the cached report. A write to the live ERP — Shoptet, the source of truth, is
 * never written here — so it is gated on `products.catalog.write` and the caller confirms
 * with the operator first.
 *
 * Deliberately `setQueryData` rather than `invalidateQueries` — invalidating would refetch
 * the whole catalogue from both live systems and throw away the very scoping the operator
 * asked for by filtering. The merged report is then marked fresh for the query's whole
 * `staleTime`, so rows outside the selection can be up to five minutes old while the cache
 * treats the report as current; syncing is how the operator refreshes what they care about,
 * a reload is how they refresh everything.
 */
export const useSyncProductPrices = () => {
  const queryClient = useQueryClient();

  return useMutation<SyncProductPricesOutcome, Error, string[]>({
    mutationFn: async (productCodes) => {
      try {
        const response = await getAuthenticatedApiClient().productPricing_Sync(
          new SyncProductPricesRequest({ productCodes }),
        );
        return {
          rows: response.rows ?? [],
          writtenCount: response.writtenCount ?? 0,
          failedCount: response.failedCount ?? 0,
          remainingCount: response.remainingCount ?? 0,
        };
      } catch (error) {
        // The generated client throws a SwaggerException on any non-200, so its message is
        // the raw transport error; the operator gets a message they can read instead.
        const envelope = readApiErrorEnvelope(error);
        throw Object.assign(new Error(GENERIC_SYNC_ERROR), { errorCode: envelope?.errorCode });
      }
    },
    onSuccess: async ({ rows: syncedRows }) => {
      // Read before cancelling: a refetch in flight (or one already queued by an invalidate)
      // is one the cancel below is about to throw away, and that has to be made good.
      const stateBeforeCancel = queryClient.getQueryState<PriceDivergenceReportData>(
        QUERY_KEYS.divergence,
      );
      const discardedAPendingRefetch =
        stateBeforeCancel?.fetchStatus === "fetching" || stateBeforeCancel?.isInvalidated === true;

      // A price save invalidates this query, and that whole-catalogue refetch across two live
      // systems is slow. React Query does not discard a resolved fetch because a
      // `setQueryData` happened meanwhile, so without this cancel the refetch lands last and
      // silently replaces the freshly force-reloaded prices with Flexi's five-minute cache —
      // exactly what the sync exists to defeat.
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.divergence });

      queryClient.setQueryData<PriceDivergenceReportData>(QUERY_KEYS.divergence, (current) =>
        current ? mergeSyncedRows(current, syncedRows) : current,
      );

      if (!discardedAPendingRefetch) return;

      // That refetch was not necessarily stale news. A save that landed AFTER this sync's
      // backend read is invisible to the merged rows, and `setQueryData` marks the report
      // fresh for the whole five-minute `staleTime`, so neither a remount nor a window focus
      // would ever correct it — the operator would watch their own write appear not to apply.
      //
      // Refetching now costs nothing in freshness: this sync force-reloaded Flexi's ceník
      // cache, so the refetch reads the same prices it just wrote there, plus the rows the
      // selection left out. The merged rows stay on screen while it runs in the background.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.divergence });
    },
  });
};
